/**
 * MaxMind Geolocation Provider
 * 
 * Uses the server-side MaxMind GeoLite2 database via the /api/geo endpoint.
 * This is the default provider that requires the server to be running.
 */

import type { IGeoProvider, GeoLookupResult } from './IGeoProvider';
import { DEFAULT_GEO_RESULT } from './IGeoProvider';
import { IP_GEOLOCATION_ENDPOINT } from '../../config/geolocation';
import { createLogger } from '../../utils/logger';
const log = createLogger('MaxMindProvider');

export class MaxMindProvider implements IGeoProvider {
    readonly name = 'MaxMind';

    /**
     * Look up the current user's IP and location via the server API
     */
    async lookupSelf(): Promise<GeoLookupResult> {
        try {
            log.log(`[${this.name}] Fetching IP metadata from ${IP_GEOLOCATION_ENDPOINT}...`);
            const response = await fetch(IP_GEOLOCATION_ENDPOINT);

            if (!response.ok) {
                throw new Error(`API request failed: ${response.statusText}`);
            }

            const data = await response.json();
            return this.mapResponse(data);
        } catch (error) {
            log.error(`[${this.name}] Self lookup failed:`, error);
            return DEFAULT_GEO_RESULT;
        }
    }

    /**
     * Look up location for a specific IP address via the server API
     */
    async lookupIp(ip: string): Promise<GeoLookupResult> {
        try {
            log.log(`[${this.name}] Looking up IP: ${ip}`);
            const url = new URL(IP_GEOLOCATION_ENDPOINT);
            url.searchParams.append('ip', ip);

            const response = await fetch(url.toString());

            if (!response.ok) {
                throw new Error(`API request failed: ${response.statusText}`);
            }

            const data = await response.json();
            return this.mapResponse(data);
        } catch (error) {
            log.error(`[${this.name}] IP lookup failed for ${ip}:`, error);
            return { ...DEFAULT_GEO_RESULT, ip };
        }
    }

    /**
     * Map MaxMind API response to standardized GeoLookupResult
     */
    private mapResponse(data: any): GeoLookupResult {
        return {
            ip: data.ip || '',
            city: data.city || null,
            country: data.country || null,
            countryCode: data.countryCode || null,
            latitude: data.latitude ?? data.lat ?? DEFAULT_GEO_RESULT.latitude,
            longitude: data.longitude ?? data.lon ?? DEFAULT_GEO_RESULT.longitude,
            org: data.org || null,
            isp: data.isp || data.org || null,
            timezone: data.timezone || null,
            hostname: null
        };
    }
}
